import type { SemanticTone } from './semantic';

/**
 * The tone colour roles and the light/dark palettes.
 *
 * ENGINEERING PLACEHOLDER — NOT A BRAND, AESTHETIC OR VISUAL-DESIGN DECISION.
 *   FE-1 Stage Work Item §4.1 D authorizes a colour token layer and states that no
 *   visual design, brand or aesthetic is approved. The values below are neutral
 *   structural placeholders chosen so the theme mechanism is demonstrable and
 *   reviewable. They are not a palette proposal and must be replaced by separately
 *   authorized design work, not by a Builder edit.
 *
 * WHY TONES ARE ROLES AND NOT COLOURS
 *   A `SemanticTone` names what a state MEANS structurally (indeterminate, aged,
 *   failed). The palette maps that role to a colour. A component that wanted to
 *   distinguish states by colour alone would have to reach through the role and
 *   name a colour directly, which this structure makes awkward on purpose — see
 *   `./semantic.ts` and the T-3 requirement (FE-1 Stage Work Item §11.2 check 4).
 */

export interface ThemePalette {
  readonly SURFACE: string;
  readonly SURFACE_MUTED: string;
  readonly SURFACE_RAISED: string;
  readonly TEXT: string;
  readonly TEXT_MUTED: string;
  readonly BORDER: string;
  readonly BORDER_STRONG: string;
  readonly FOCUS_RING: string;
  /** One colour per tone role. Never the sole carrier of a state distinction. */
  readonly TONE: Readonly<Record<SemanticTone, string>>;
}

export const LIGHT_PALETTE: ThemePalette = Object.freeze({
  SURFACE: '#ffffff',
  SURFACE_MUTED: '#f4f5f7',
  SURFACE_RAISED: '#ffffff',
  TEXT: '#14171a',
  TEXT_MUTED: '#5a6570',
  BORDER: '#d3d8de',
  BORDER_STRONG: '#9aa4af',
  FOCUS_RING: '#2f5d8a',
  TONE: Object.freeze({
    NEUTRAL: '#4a5560',
    INDETERMINATE: '#6b5a2e',
    CAUTION: '#7a5320',
    FAILURE: '#8c2f2f',
    ABSENCE: '#5a6570',
  }),
});

export const DARK_PALETTE: ThemePalette = Object.freeze({
  SURFACE: '#14171a',
  SURFACE_MUTED: '#1d2126',
  SURFACE_RAISED: '#22272d',
  TEXT: '#f2f4f6',
  TEXT_MUTED: '#a8b1ba',
  BORDER: '#343b43',
  BORDER_STRONG: '#556069',
  FOCUS_RING: '#8fb6dd',
  TONE: Object.freeze({
    NEUTRAL: '#b6c0ca',
    INDETERMINATE: '#d9c48a',
    CAUTION: '#e0b070',
    FAILURE: '#e08585',
    ABSENCE: '#a8b1ba',
  }),
});
